// Visual designer model: {w, h, root}; nodes: mull/split {t, dir: "cols"|"rows", sizes, kids}, unit {t:"unit", kind, sec, shape_id, ext_color}, sec {t:"sec", type, hinge, swing, slide, grid, screen_id}.
import { layoutText, parseLayout } from "@/lib/layouts";

// Design → catalog layout text (first unit's sections; X = any opening type, O = fixed). Sizes are not kept (equal split).
export function toLayout(d) {
  const unitNode = d.root.t === "unit" ? d.root : d.root.kids?.find((k) => k.t === "unit") || d.root;
  const conv = (n) => (n.t === "sec" ? ["cell", n.type === "fixed" ? "O" : "X"] : [n.dir, n.kids.map(conv)]);
  return layoutText(conv(unitNode.t === "unit" ? unitNode.sec : unitNode));
}

export const MIN = 12;
export const SEC_TYPES = [["fixed", "Fixed"], ["casement", "Casement"], ["awning", "Awning"], ["hopper", "Hopper"], ["tilt_turn", "Tilt-Turn"], ["sliding", "Sliding"], ["door", "Door leaf"]];
const clone = (x) => JSON.parse(JSON.stringify(x));
export const sec = (type = "fixed", extra = {}) => ({ t: "sec", type, hinge: "left", swing: "in", slide: "right", grid: null, screen_id: null, ...extra });
export const unit = (kind = "window") => ({ t: "unit", kind, sec: sec(kind === "door" ? "door" : "fixed"), shape_id: null });
export const newDesign = (kind = "window", w = 72, h = 60) => ({ w, h, root: unit(kind) });

export const get = (root, path) => path.reduce((n, i) => (n.t === "unit" ? n.sec : n.kids[i]), root);
const childOf = (n, i) => (n.t === "unit" ? n.sec : n.kids[i]);
function setAt(d, path, fn) {
  const out = clone(d);
  if (!path.length) { out.root = fn(out.root); return out; }
  let n = out.root;
  for (const i of path.slice(0, -1)) n = childOf(n, i);
  const last = path[path.length - 1];
  if (n.t === "unit") n.sec = fn(n.sec); else n.kids[last] = fn(n.kids[last]);
  return out;
}

function sizesOf(n, total) {
  const s = (n.sizes || []).map(Number);
  return s.length === n.kids.length && Math.abs(s.reduce((a, b) => a + b, 0) - total) < 0.6 ? s : n.kids.map(() => total / n.kids.length);
}

// Flatten: units, sections, dividers with rects in inches (top-left origin). Paths: unit -> sec uses index 0.
export function layout(d) {
  const out = { units: [], secs: [], dividers: [] };
  const walk = (n, path, x, y, w, h, u) => {
    if (n.t === "unit") {
      const uu = { node: n, path, x, y, w, h };
      out.units.push(uu);
      return walk(n.sec, [...path, 0], x, y, w, h, uu);
    }
    if (n.t === "sec") return out.secs.push({ node: n, path, x, y, w, h, unit: u });
    const cols = n.dir === "cols";
    const sizes = sizesOf(n, cols ? w : h);
    let cx = x, cy = y;
    n.kids.forEach((k, i) => {
      const kw = cols ? sizes[i] : w, kh = cols ? h : sizes[i];
      walk(k, [...path, i], cx, cy, kw, kh, u);
      if (i < n.kids.length - 1) out.dividers.push({ path, i, cols, mull: n.t === "mull", x: cols ? cx + kw : x, y: cols ? y : cy + kh, len: cols ? h : w });
      if (cols) cx += kw; else cy += kh;
    });
  };
  walk(d.root, [], 0, 0, Number(d.w) || 1, Number(d.h) || 1, null);
  return out;
}

// Smallest size a node can take along a direction.
function minSize(n, cols) {
  if (n.t === "sec") return MIN;
  if (n.t === "unit") return minSize(n.sec, cols);
  const m = n.kids.map((k) => minSize(k, cols));
  return (n.dir === "cols") === cols ? m.reduce((a, b) => a + b, 0) : Math.max(...m);
}
export const minOf = (d) => ({ w: minSize(d.root, true), h: minSize(d.root, false) });

export function rectOf(d, path) {
  const L = layout(d);
  return [...L.secs, ...L.units].find((r) => r.path.join() === path.join());
}

// Move divider i of the container at path by delta inches; returns [design, clamped].
export function resize(d, path, i, delta, startSizes) {
  const r = path.length ? containerRect(d, path) : { w: d.w, h: d.h };
  const n = get(d.root, path);
  const cols = n.dir === "cols";
  const sizes = [...(startSizes || sizesOf(n, cols ? r.w : r.h))];
  const lo = minSize(n.kids[i], cols), hi = minSize(n.kids[i + 1], cols);
  let a = sizes[i] + delta;
  const pair = sizes[i] + sizes[i + 1];
  const clamped = a < lo || pair - a < hi;
  a = Math.min(Math.max(a, lo), pair - hi);
  sizes[i] = Math.round(a * 8) / 8;
  sizes[i + 1] = Math.round((pair - sizes[i]) * 8) / 8;
  return [setAt(d, path, (x) => ({ ...x, sizes })), clamped];
}

function containerRect(d, path) {
  const L = layout(d);
  const hit = [...L.units, ...L.secs].find((r) => r.path.join().startsWith(path.join()) && r.path.length > path.length);
  if (!hit) return { w: d.w, h: d.h };
  // rebuild bounds of the container from its leaves
  const leaves = [...L.units, ...L.secs].filter((r) => r.path.slice(0, path.length).join() === path.join() && r.path.length > path.length);
  const x1 = Math.min(...leaves.map((r) => r.x)), y1 = Math.min(...leaves.map((r) => r.y));
  return { w: Math.max(...leaves.map((r) => r.x + r.w)) - x1, h: Math.max(...leaves.map((r) => r.y + r.h)) - y1 };
}

export function setSize(d, path, i, value) {
  const n = get(d.root, path);
  const r = path.length ? containerRect(d, path) : { w: d.w, h: d.h };
  const sizes = sizesOf(n, n.dir === "cols" ? r.w : r.h);
  const j = i < sizes.length - 1 ? i : i - 1;
  const delta = i === j ? value - sizes[i] : sizes[i] - value;
  return resize(d, path, j, delta, sizes);
}

export function split(d, path, dir) {
  const r = rectOf(d, path);
  const total = dir === "cols" ? r.w : r.h;
  if (total < MIN * 2) return [d, true];
  return [setAt(d, path, (s) => ({ t: "split", dir, sizes: [total / 2, total / 2], kids: [s, clone({ ...s, screen_id: null })] })), false];
}

export function merge(d, path) {
  if (path.length < 2) return d;
  const parent = path.slice(0, -1);
  const p = get(d.root, parent);
  if (p.t !== "split") return d;
  return setAt(d, parent, () => p.kids.find((k) => k.t === "sec") || p.kids[0]);
}

export const patchAt = (d, path, p) => setAt(d, path, (n) => ({ ...n, ...p }));

export function addUnit(d, side, size, kind = "window") {
  const cols = side === "left" || side === "right";
  const u = unit(kind);
  const out = clone(d);
  const first = side === "left" || side === "top";
  const r = out.root;
  const span = cols ? out.w : out.h;
  if (r.t === "mull" && (r.dir === "cols") === cols) {
    const sizes = sizesOf(r, span);
    r.kids = first ? [u, ...r.kids] : [...r.kids, u];
    r.sizes = first ? [size, ...sizes] : [...sizes, size];
  } else {
    out.root = { t: "mull", dir: cols ? "cols" : "rows", sizes: first ? [size, span] : [span, size], kids: first ? [u, r] : [r, u] };
  }
  if (cols) out.w = Number(out.w) + size; else out.h = Number(out.h) + size;
  return out;
}

export function removeUnit(d, unitPath) {
  if (!unitPath.length) return d;
  const parent = unitPath.slice(0, -1), i = unitPath[unitPath.length - 1];
  const p = get(d.root, parent);
  const cols = p.dir === "cols";
  const r = parent.length ? containerRect(d, parent) : { w: d.w, h: d.h };
  const sizes = sizesOf(p, cols ? r.w : r.h);
  const gone = sizes[i];
  let out = setAt(d, parent, (n) => {
    const kids = n.kids.filter((_, k) => k !== i), sz = sizes.filter((_, k) => k !== i);
    return kids.length === 1 ? kids[0] : { ...n, kids, sizes: sz };
  });
  if (!parent.length) out = { ...out, [cols ? "w" : "h"]: (cols ? d.w : d.h) - gone };
  return out;
}

// Proportional resize (templates, overall size edits).
export function scale(d, W, H) {
  const fx = W / d.w, fy = H / d.h;
  const walk = (n) => {
    if (n.t === "unit") return { ...n, sec: walk(n.sec) };
    if (n.t === "sec") return n;
    return { ...n, sizes: (n.sizes || []).map((s) => Math.round(s * (n.dir === "cols" ? fx : fy) * 8) / 8), kids: n.kids.map(walk) };
  };
  const out = { ...d, w: W, h: H, root: walk(d.root) };
  // rounding fix: rebalance last sizes so they sum to the parent
  const fix = (n, w, h) => {
    if (n.t === "unit") return fix(n.sec, w, h);
    if (n.t === "sec") return;
    const total = n.dir === "cols" ? w : h;
    if (n.sizes?.length === n.kids.length) n.sizes[n.sizes.length - 1] = Math.round((total - n.sizes.slice(0, -1).reduce((a, b) => a + b, 0)) * 8) / 8;
    const s = sizesOf(n, total);
    n.kids.forEach((k, i) => fix(k, n.dir === "cols" ? s[i] : w, n.dir === "cols" ? h : s[i]));
  };
  fix(out.root, W, H);
  return out;
}

// Phase 10 layout text ("O|(X/O)") or slider config ("XO") -> design.
export function fromLayout(text, w, h, lineCategory = "casement", isSlider = false) {
  const opType = isSlider ? "sliding" : lineCategory === "awning" ? "awning" : lineCategory === "swing_door" ? "door" : "casement";
  const src = isSlider ? (text || "XO").split("").join("|") : text || "X";
  let t;
  try { t = parseLayout(src); } catch { t = ["cell", "X"]; }
  let xs = 0;
  const conv = (n, W, H) => {
    if (n[0] === "cell") {
      xs += 1;
      return sec(n[1] === "X" ? opType : "fixed", { hinge: xs % 2 ? "left" : "right" });
    }
    const cols = n[0] === "cols", total = cols ? W : H, s = total / n[1].length;
    return { t: "split", dir: cols ? "cols" : "rows", sizes: n[1].map(() => s), kids: n[1].map((k) => conv(k, cols ? s : W, cols ? H : s)) };
  };
  return { w, h, root: { t: "unit", kind: lineCategory.includes("door") ? "door" : "window", sec: conv(t, w, h), shape_id: null } };
}
